import { PageContent, PortfolioSelection, PortfolioValue } from '@components/index';
import { Home } from '@mui/icons-material';
import { Box, Breadcrumbs, Container, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ScreenState } from '@src/app/enums';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { selectAssetAllocation } from '@src/app/store/reducers/assetAllocation/selectors';
import { getAssetAllocationAsync } from '@src/app/store/reducers/assetAllocation/thunks';
import { FC, useEffect } from 'react';
import AssetCardContainer from './AssetCardContainer';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { selectHoldings } from '@src/app/store/reducers/holdings/selectors';
import { setAsOfDate } from '@src/app/store/reducers/holdings';
import DoughnutChart from './DoughnutChart';
import { useWindow } from '@src/app/hooks/useWindow';
import { DateRange } from '@src/app/store';
import DateRangePicker from '@components/dateRange';
import { LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import { homeUrl } from '@src/app/utils/UrlBuilder';
import { useHistory } from 'react-router';

const AssetAllocation: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const history = useHistory();

  const { resizing } = useWindow();
  const { portfoliosSelected, portfolioInfoState } = useAppSelector(selectPortfolios);
  const { asOfDate } = useAppSelector(selectHoldings);
  const { view, state, totalValue } = useAppSelector(selectAssetAllocation);

  const isBetweenXsAndSm = useMediaQuery(theme.breakpoints.between('xs', 'sm'));

  const setDate = (date: DateRange) => {
    dispatch(setAsOfDate(date));
  };

  const fetchAssetAllocationAsync = async () => {
    await dispatch(
      getAssetAllocationAsync({
        asOfDate: asOfDate.dateTo,
        clientPortfolios: portfoliosSelected,
      })
    );
  };

  useEffect(() => {
    if (state !== ScreenState.SuccessDataLoaded) {
      fetchAssetAllocationAsync();
    }
  }, []);

  useEffect(() => {
    if (state !== ScreenState.Loading) {
      fetchAssetAllocationAsync();
    }
  }, [asOfDate, portfoliosSelected]);

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="24px" mb={6}>
        <Box display="flex" gap="16px" mt={2} sx={{ alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
          <Breadcrumbs>
            <Typography
              onClick={() => history.push(homeUrl())}
              sx={{
                display: 'flex',
                alignItems: 'center',
                ':hover': {
                  cursor: 'pointer',
                },
              }}
              color="inherit"
            >
              <Home sx={{ mr: 0.5 }} fontSize="small" />
              Dashboard
            </Typography>
            <Typography fontWeight={600} fontSize="20px" color="#222222">
              Asset Allocation
            </Typography>
          </Breadcrumbs>

          <Box flex={1} />

          <Box display="flex" gap="16px">
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker isDateOnly dateRange={asOfDate} setDateRange={(dateRange: DateRange) => setDate(dateRange)} />
            </LocalizationProvider>
            <PortfolioSelection allowCombining={false} disabled={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading} />
          </Box>
        </Box>

        <PageContent isLoading={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading}>
          <PortfolioValue totalValue={totalValue} />
          <Divider />
          {view.cards.length === 0 ? (
            <Grid container justifyContent="center">
              <Grid item>
                <DoughnutChart
                  width={isBetweenXsAndSm ? '270' : '360'}
                  height={isBetweenXsAndSm ? '185' : '345'}
                  pieRadius={isBetweenXsAndSm ? '80' : '160'}
                  chartData={view.charts}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container direction={{ sm: 'column', lg: 'row' }} mt={2} spacing={4} alignItems="center" justifyContent="center">
              <Grid item md={12} lg={6} textAlign="center">
                {!resizing && (
                  <DoughnutChart
                    width={isBetweenXsAndSm ? '270' : '360'}
                    height={isBetweenXsAndSm ? '185' : '345'}
                    pieRadius={isBetweenXsAndSm ? '80' : '160'}
                    chartData={view.charts}
                  />
                )}
              </Grid>
              <Grid item container md={12} lg={6} direction="column" spacing={1}>
                <AssetCardContainer assetCards={view.cards} />
              </Grid>
            </Grid>
          )}
        </PageContent>
      </Box>
    </Container>
  );
};

export default AssetAllocation;
